// adminfrontend/src/admin_ui/pages/AdminForecastPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import adminAnalyticsApi from "../services/adminAnalyticsApi";
import adminPosApi from "../services/adminPosApi";
import "../styles/Admin.css";

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function productLabel(p) {
  if (!p) return "";
  const id = p.id ?? p.product_id ?? p.productId ?? "";
  const sku = p.ucx_sku ?? p.ucxSku ?? p.sku ?? "";
  const name = p.name ?? p.product_name ?? p.productName ?? "";
  const cat = p.category ?? "";
  const bits = [
    id ? `#${id}` : null,
    sku ? sku : null,
    name ? name : null,
    cat ? `(${cat})` : null,
  ].filter(Boolean);
  return bits.join(" — ");
}

export default function AdminForecastPage({ adminEmail, onLogout }) {
  // Forecast inputs
  const [productId, setProductId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [daysBack, setDaysBack] = useState(28);
  const [horizonDays, setHorizonDays] = useState(7);

  // Results filter
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  // Jobs/results
  const [jobs, setJobs] = useState([]);
  const [rows, setRows] = useState([]);

  // Product search/dropdown
  const [productSearch, setProductSearch] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [productTotal, setProductTotal] = useState(0);

  // Page state
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const normalizedJobs = useMemo(() => (Array.isArray(jobs) ? jobs : []), [jobs]);
  const normalizedRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const normalizedProducts = useMemo(
    () => (Array.isArray(productOptions) ? productOptions : []),
    [productOptions]
  );

  const selectedProduct = useMemo(() => {
    const pid = num(productId);
    if (!pid) return null;
    return normalizedProducts.find((p) => Number(p?.id) === pid) || null;
  }, [productId, normalizedProducts]);

  async function loadJobs() {
    const data = await adminAnalyticsApi.listForecastJobs({ limit: 25, offset: 0 }).catch(() => null);
    if (!data?.ok) return;
    const list = data.jobs ?? data.rows ?? data.items ?? [];
    setJobs(Array.isArray(list) ? list : []);
  }

  async function loadResults() {
    setError("");
    const params = {
      productId: productId ? num(productId) : undefined,
      branchId: branchId ? num(branchId) : undefined,
      from: from || undefined,
      to: to || undefined,
      limit: 200,
      offset: 0,
    };

    const data = await adminAnalyticsApi.listForecastResults(params).catch(() => null);
    if (!data?.ok) {
      setRows([]);
      return;
    }
    const list = data.rows ?? data.results ?? data.items ?? [];
    setRows(Array.isArray(list) ? list : []);
  }

  async function refreshAll() {
    setLoading(true);
    setError("");
    setOkMsg("");
    try {
      await Promise.all([loadJobs(), loadResults()]);
    } catch (e) {
      console.error("[AdminForecastPage] refresh error:", e);
      setError("Unable to load forecast data. Check login or server.");
    } finally {
      setLoading(false);
    }
  }

  // Simple debounce for product search
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const q = (productSearch || "").trim();
    if (!q) {
      setProductOptions([]);
      setProductTotal(0);
      return;
    }

    debounceRef.current = setTimeout(() => {
      searchProducts(q);
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearch]);

  async function searchProducts(q) {
    setProductLoading(true);
    try {
      // Uses GET /api/identity/products (same endpoint POS uses) :contentReference[oaicite:1]{index=1}
      const data = await adminPosApi
        .searchProducts({ search: q, limit: 20, offset: 0, posOnly: "0" })
        .catch(() => null);

      if (!data?.ok) {
        setProductOptions([]);
        setProductTotal(0);
        return;
      }

      const list = Array.isArray(data.products)
        ? data.products
        : Array.isArray(data.items)
        ? data.items
        : [];

      setProductOptions(list);
      setProductTotal(Number(data.total ?? list.length ?? 0));
    } catch (e) {
      console.error("[AdminForecastPage] product search error:", e);
      setProductOptions([]);
      setProductTotal(0);
    } finally {
      setProductLoading(false);
    }
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runJob() {
    setMutating(true);
    setError("");
    setOkMsg("");
    try {
      const payload = {
        productId: productId ? num(productId) : undefined,
        branchId: branchId ? num(branchId) : undefined,
        daysBack: Number(daysBack) || 28,
        horizonDays: Number(horizonDays) || 7,
      };

      const data = await adminAnalyticsApi.runForecastJob(payload).catch(() => null);
      if (!data?.ok) throw new Error(data?.error || "run_failed");

      setOkMsg(`Forecast job ${data.jobId || ""} completed.`);
      await refreshAll();
    } catch (e) {
      console.error("[AdminForecastPage] run error:", e);
      setError("Unable to run forecast. Ensure POS has sales history and server is reachable.");
    } finally {
      setMutating(false);
    }
  }

  function onPickProduct(e) {
    const val = e.target.value;
    if (!val) {
      setProductId("");
      return;
    }
    // Expect val to be numeric ID from <select>
    const pid = val.replace(/[^\d]/g, "");
    setProductId(pid);
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Analytics — Forecast</h1>
            <p className="ucx-admin-subtitle">MA7 baseline forecasts stored in forecast_results.</p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={refreshAll} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Run Forecast Job</div>

          <div className="ucx-admin-filters ucx-admin-filters-inventory" style={{ alignItems: "flex-start" }}>
            <div style={{ minWidth: 320 }}>
              <input
                className="ucx-admin-input-compact"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search product (name / SKU)"
              />
              <div className="ucx-small" style={{ marginTop: 6 }}>
                {productSearch.trim()
                  ? productLoading
                    ? "Searching…"
                    : `${normalizedProducts.length} shown (total: ${productTotal})`
                  : "Type to search products (optional)"}
              </div>

              {normalizedProducts.length > 0 && (
                <select
                  value={productId}
                  onChange={onPickProduct}
                  style={{ marginTop: 8, width: "100%" }}
                >
                  <option value="">All products (no filter)</option>
                  {normalizedProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {productLabel(p)}
                    </option>
                  ))}
                </select>
              )}

              {productId && (
                <div className="ucx-small" style={{ marginTop: 6 }}>
                  Selected: <b>{selectedProduct ? productLabel(selectedProduct) : `#${productId}`}</b>
                </div>
              )}
            </div>

            <input
              className="ucx-admin-input-compact"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value.replace(/[^\d]/g, ""))}
              placeholder="Branch ID (optional)"
              style={{ minWidth: 220 }}
            />

            <input
              className="ucx-admin-input-compact"
              type="number"
              min="7"
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
              placeholder="Days back"
              style={{ width: 140 }}
            />

            <input
              className="ucx-admin-input-compact"
              type="number"
              min="1"
              value={horizonDays}
              onChange={(e) => setHorizonDays(Number(e.target.value))}
              placeholder="Horizon days"
              style={{ width: 140 }}
            />

            <button type="button" className="ucx-btn ucx-btn-primary" onClick={runJob} disabled={mutating}>
              {mutating ? "Running…" : "Run forecast"}
            </button>
          </div>

          {okMsg && <div className="ucx-admin-status-success">{okMsg}</div>}
          {error && <div className="ucx-admin-status-error">{error}</div>}
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Results Filter</div>
          <div className="ucx-admin-filters ucx-admin-filters-inventory">
            <label className="ucx-small">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <label className="ucx-small">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            <button type="button" className="ucx-btn ucx-btn-primary" onClick={loadResults} disabled={loading}>
              Apply
            </button>
            <span className="ucx-small">Rows: {normalizedRows.length}</span>
          </div>
        </div>

        <div className="ucx-admin-table-wrapper">
          <table className="ucx-admin-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Product</th>
                <th>Branch</th>
                <th>Qty</th>
                <th>Method</th>
                <th>Job</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="ucx-admin-empty">
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && normalizedRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="ucx-admin-empty">
                    No forecast results found.
                  </td>
                </tr>
              )}

              {normalizedRows.map((r, idx) => (
                <tr key={r.id || `${r.forecast_date}-${idx}`}>
                  <td>{r.forecast_date || r.date || "-"}</td>
                  <td>{r.product_id ?? r.productId ?? "-"}</td>
                  <td>{r.branch_id ?? r.branchId ?? "-"}</td>
                  <td>{r.forecast_qty ?? r.qty ?? "-"}</td>
                  <td>{r.method || "ma7"}</td>
                  <td>{r.job_id ?? r.jobId ?? "-"}</td>
                  <td>{r.created_at ? new Date(r.created_at).toLocaleString() : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Recent Jobs</div>
          <div className="ucx-admin-table-wrapper">
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Params</th>
                  <th>Started</th>
                  <th>Finished</th>
                </tr>
              </thead>
              <tbody>
                {normalizedJobs.length === 0 && (
                  <tr>
                    <td colSpan={5} className="ucx-admin-empty">
                      No jobs loaded.
                    </td>
                  </tr>
                )}
                {normalizedJobs.map((j) => (
                  <tr key={j.id}>
                    <td>{j.id}</td>
                    <td>{j.status || "-"}</td>
                    <td className="ucx-small">{j.params ? JSON.stringify(j.params) : "-"}</td>
                    <td>{j.started_at ? new Date(j.started_at).toLocaleString() : "-"}</td>
                    <td>{j.finished_at ? new Date(j.finished_at).toLocaleString() : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ucx-small" style={{ marginTop: 8 }}>
            Tip: Search a product, select it, run forecast, then filter results by date range.
          </div>
        </div>
      </div>
    </section>
  );
}
